import { navigateBack } from '@services/navigationService';
import {
  CPQ_SESSION_ID_KEY,
  CPQ_RESUME_TOKEN_KEY,
  CPQ_SESSION_STORED_AT_KEY,
  CPQ_QUALIFICATION_CACHE_KEY,
  CPQ_QUALIFICATION_FLOW_KEY,
  CPQ_CHECKOUT_STATE_KEY,
  CPQ_ORDER_DATA_KEY,
} from '@t/session.types';

const CPQ_LOCAL_STORAGE_KEYS = [
  CPQ_SESSION_ID_KEY,
  CPQ_RESUME_TOKEN_KEY,
  CPQ_SESSION_STORED_AT_KEY,
  CPQ_QUALIFICATION_CACHE_KEY,
  CPQ_QUALIFICATION_FLOW_KEY,
  CPQ_CHECKOUT_STATE_KEY,
  CPQ_ORDER_DATA_KEY,
] as const;

const NOVUS_HOMEPAGE_URL = 'https://www.novusnow.ca/';

export const clearAllCpqStorage = (): void => {
  CPQ_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const navigateOut = (homepageUrl?: string): void => {
  if (document.referrer.toLowerCase().includes('novus')) {
    window.history.back();
    return;
  }
  window.location.href = homepageUrl || NOVUS_HOMEPAGE_URL;
};

/**
 * @param homepageUrl Destination when the widget isn't embedded on the tenant's own
 * site (referrer check above) — pass the tenant's `branding.homepageUrl` from
 * TenantConfigProvider. Falls back to the historical Novus URL when omitted, so
 * callers that haven't been updated (and existing tests) keep their prior behavior.
 */
export const closeCpqApp = (onClose?: () => void, homepageUrl?: string): void => {
  onClose?.();
  window.dispatchEvent(new CustomEvent('cpq:close', { bubbles: true }));
  navigateOut(homepageUrl);
  clearAllCpqStorage();
};

interface StartNewOrderDeps {
  sessionId: string;
  abandonCart: () => Promise<void>;
  resetCheckout: () => void;
  resetConfig: () => void;
  removeCartQueries: () => void;
  clearQualification: () => void;
}

/**
 * Resets the current session to SELECT step and clears all local state.
 * Reuses the existing session (preserves address linkage) rather than
 * creating a new one.
 *
 * Shared by EntryRouter (app-load "Start New") and CheckAvailabilityDialog (post-SQ "Start New").
 */
export const startNewOrder = async (deps: StartNewOrderDeps): Promise<void> => {
  // Abandon the existing cart before resetting
  await deps.abandonCart();

  // Reset session to SELECT step on the BE (keeps address linked)
  await navigateBack(deps.sessionId, 'SELECT');

  // Clear all local state
  deps.clearQualification();
  deps.resetCheckout();
  deps.resetConfig();
  deps.removeCartQueries();

  // Remove persisted localStorage values so a fresh order doesn't restore stale state
  // Note: CPQ_QUALIFICATION_CACHE_KEY is intentionally preserved so the user's address is retained
  localStorage.removeItem(CPQ_ORDER_DATA_KEY);
};
